import { useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, FileText, ListChecks } from "lucide-react";
import posthog from "posthog-js";
import { useState } from "react";

import EvaluationPreset, {
  EvaluationPresetStep,
} from "@/components/PromptTemplate/PresetEvaluationsDialog/EvaluationPreset";
import { BrandVisibilityPresetSubmitPayload } from "@/components/PromptTemplate/PresetEvaluationsDialog/EvaluationPreset/brandVisibilityTypes";
import {
  dedupeModelLabels,
  ModelComparisonSelection,
} from "@/components/PromptTemplate/PresetEvaluationsDialog/modelComparisonTypes";
import PresetGrid from "@/components/PromptTemplate/PresetEvaluationsDialog/PresetGrid";
import SelectTemplate from "@/components/SelectTemplate";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import useCreateDefaultSmartTable from "@/components/UnifiedRegistry/NewItemButton/hooks/useCreateDefaultSmartTable";
import { OPEN_VIBECHAT_EVENT } from "@/components/VibeChat/constants";
import { chatStoreInstance } from "@/components/VibeChat/hooks";
import { handleVibeChatToolResultInvalidations } from "@/components/VibeChat/utils/invalidateSmartTableQueries";
import { useAuth } from "@/context/auth-context";
import { useUser } from "@/context/user-context";
import { ToastType } from "@/enums";
import { usePrompt } from "@/queries";
import { Entity, EntityType, getEntityNumericId } from "@/queries/folder-entities/types";
import {
  EvaluationPresetType,
  type SmartSheet,
  useCreateBrandVisibilityPreset,
  useCreateCustomPreset,
  useCreateEvaluationPreset,
  useCreateModelComparisonPreset,
  useInitializeSheetBrandVisibilityPreset,
  useInitializeSheetCustomPreset,
  useInitializeSheetEvaluationPreset,
  useInitializeSheetModelComparisonPreset,
} from "@/queries/smart-tables";
import { PromptVersion } from "@/types/apiGetters";
import { displayErrorToast, displayToast } from "@/utils/toast";
import { convertWorkspaceUrlsToTagsAsync } from "@/utils/workspaceUrlTags";

export interface SmartTableCreateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  folderId?: number | null;
  openInNewTab?: boolean;
  presetTarget?: {
    tableId: string;
    sheetId: string;
    onInitialized?: (sheet: SmartSheet) => void;
  };
}

type CreateDialogStep = "presets" | "prompt" | EvaluationPresetStep;

const stepTitle = (step: CreateDialogStep) => {
  if (step === "prompt") {
    return "Choose a prompt";
  }
  if (step === "model-comparison") {
    return "Model comparison";
  }
  if (step === "GROUND_TRUTH_COMPARE") {
    return "Ground truth check";
  }
  if (step === "STRUCTURAL") {
    return "Structure check";
  }
  if (step === "custom") {
    return "Custom prompt table";
  }
  if (step === "brand-visibility") {
    return "Brand visibility";
  }
  if (step === "generate-with-ai") {
    return "Generate with AI";
  }
  return "Create table";
};

const stepDescription = (step: CreateDialogStep) => {
  if (step === "prompt") {
    return "Select the prompt version this table should use. Presets create the first columns for you.";
  }
  if (step === "model-comparison") {
    return "Run one prompt across multiple models and compare output, cost, and latency.";
  }
  if (step === "GROUND_TRUTH_COMPARE") {
    return "Compare generated output against a trusted answer with focused pass/fail checks.";
  }
  if (step === "STRUCTURAL") {
    return "Verify that outputs follow your required format using clear structure checks.";
  }
  if (step === "custom") {
    return "Create input columns and one prompt output column connected to those variables.";
  }
  if (step === "brand-visibility") {
    return "Check whether AI answers mention your brand, domain, or competitors.";
  }
  if (step === "generate-with-ai") {
    return "Tell Wrangler AI what you want, and it will generate the table setup.";
  }
  return "Choose how you want to start. You can edit everything after the table is created.";
};

const modelComparisonTableTitle = (promptName?: string) =>
  promptName ? `${promptName} model comparison` : "Model comparison";

const customTableTitle = (promptName?: string) =>
  promptName ? `${promptName} custom table` : "Custom prompt table";

const brandVisibilityTableTitle = "Brand visibility";

const buildGenerateWithAIMessage = ({
  userRequest,
  presetTarget,
  folderId,
}: {
  userRequest: string;
  presetTarget?: SmartTableCreateDialogProps["presetTarget"];
  folderId?: number | null;
}) => {
  const targetInstruction = presetTarget
    ? `Use the current Smart Table and initialize or update the current empty sheet. smart_table_id: ${presetTarget.tableId}. sheet_id: ${presetTarget.sheetId}. Do not create a different table unless the current sheet cannot be used.`
    : `Create a new Smart Table${folderId ? ` in folder_id ${folderId}` : ""}.`;

  return `You are Wrangler AI helping create a PromptLayer Smart Table.

${targetInstruction}

Build a runnable Smart Table from the user's request. Prefer a useful default structure instead of asking follow-up questions. Create input/example rows, relevant output columns, dependencies, and run the table when possible. If the request is vague, choose a simple evaluation-style table with example inputs, one model output column, and one check/score column where appropriate.

User request:
${userRequest}`;
};

const evaluationTableTitle = (presetType: EvaluationPresetType, promptName?: string) => {
  const defaultTitle =
    presetType === "GROUND_TRUTH_COMPARE" ? "Ground truth check" : "Structure check";
  if (!promptName) {
    return defaultTitle;
  }

  const suffix = presetType === "GROUND_TRUTH_COMPARE" ? "ground truth check" : "structure check";
  return `${promptName} ${suffix}`;
};

const getPromptVariantLabel = (
  prompt: Entity<EntityType.PROMPT> | null,
  promptVersion?: PromptVersion,
) => {
  if (!prompt) {
    return "Choose a prompt to continue.";
  }
  if (prompt.label) {
    return `Using release label “${prompt.label}”.`;
  }
  if (typeof prompt.version_number === "number") {
    return `Using version ${prompt.version_number}.`;
  }
  if (promptVersion) {
    return `Using latest version (${promptVersion.number}).`;
  }
  return "Loading selected prompt…";
};

const PromptStep = ({
  selectedPrompt,
  promptVersion,
  isLoadingPrompts,
  isLoadingPromptVersion,
  onSelectPrompt,
  onContinue,
}: {
  selectedPrompt: Entity<EntityType.PROMPT> | null;
  promptVersion?: PromptVersion;
  isLoadingPrompts: boolean;
  isLoadingPromptVersion: boolean;
  onSelectPrompt: (prompt: Entity<EntityType.PROMPT | EntityType.SNIPPET>) => void;
  onContinue: () => void;
}) => {
  const canContinue = Boolean(selectedPrompt && promptVersion && !isLoadingPromptVersion);
  let statusText = getPromptVariantLabel(selectedPrompt, promptVersion);
  if (selectedPrompt && isLoadingPromptVersion) {
    statusText = "Loading selected prompt…";
  } else if (selectedPrompt && !promptVersion) {
    statusText = "We couldn’t load that prompt version. Choose another version or label.";
  }

  return (
    <div className="flex min-h-0 flex-col">
      <div className="max-h-[58vh] overflow-y-auto px-4 py-4 sm:px-5">
        <div className="space-y-4">
          <div>
            <label
              className="mb-2 block text-sm font-medium text-gray-900"
              htmlFor="smart-table-prompt"
            >
              Prompt
            </label>
            <SelectTemplate
              template={selectedPrompt ?? undefined}
              isLoading={isLoadingPrompts}
              onSelect={onSelectPrompt}
              selectLatest={false}
            />
          </div>

          <div className="rounded-xl border border-gray-200 bg-gray-50/80 p-3">
            <div className="flex gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white text-gray-600 ring-1 ring-gray-100">
                <ListChecks className="h-4 w-4" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900">What happens next</p>
                <p className="mt-1 text-xs leading-5 text-gray-500">
                  Choose a prompt, then pick a version or release label from the prompt picker.
                  We’ll use that selection to detect input variables and create the preset table in
                  your current folder.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-gray-100 bg-gray-50/60 px-4 py-3 sm:px-5">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
          <p className="text-xs text-gray-500">{statusText}</p>
          <Button
            type="button"
            onClick={onContinue}
            disabled={!canContinue || isLoadingPromptVersion}
          >
            Continue
          </Button>
        </div>
      </div>
    </div>
  );
};

const SmartTableCreateDialog = ({
  open,
  onOpenChange,
  folderId,
  openInNewTab = false,
  presetTarget,
}: SmartTableCreateDialogProps) => {
  const [step, setStep] = useState<CreateDialogStep>("presets");
  const [selectedPreset, setSelectedPreset] = useState<EvaluationPresetStep | null>(null);
  const [selectedPrompt, setSelectedPrompt] = useState<Entity<EntityType.PROMPT> | null>(null);
  const [isSubmittingGenerateWithAI, setIsSubmittingGenerateWithAI] = useState(false);

  const auth = useAuth();
  const userToken = auth?.userToken ?? "";
  const workspaceId = useUser().activeWorkspaceId;
  const queryClient = useQueryClient();

  const { create: createBlankTable, isCreating: isCreatingBlankTable } = useCreateDefaultSmartTable(
    folderId ?? undefined,
    openInNewTab,
  );
  const createModelComparisonPreset = useCreateModelComparisonPreset({ openInNewTab });
  const createEvaluationPreset = useCreateEvaluationPreset({ openInNewTab });
  const createCustomPreset = useCreateCustomPreset({ openInNewTab });
  const createBrandVisibilityPreset = useCreateBrandVisibilityPreset({ openInNewTab });
  const initializeModelComparisonPreset = useInitializeSheetModelComparisonPreset({
    tableId: presetTarget?.tableId,
    sheetId: presetTarget?.sheetId,
  });
  const initializeEvaluationPreset = useInitializeSheetEvaluationPreset({
    tableId: presetTarget?.tableId,
    sheetId: presetTarget?.sheetId,
  });
  const initializeCustomPreset = useInitializeSheetCustomPreset({
    tableId: presetTarget?.tableId,
    sheetId: presetTarget?.sheetId,
  });
  const initializeBrandVisibilityPreset = useInitializeSheetBrandVisibilityPreset({
    tableId: presetTarget?.tableId,
    sheetId: presetTarget?.sheetId,
  });
  const isInitializingExistingSheet = Boolean(presetTarget);

  const promptRegistryId = selectedPrompt ? getEntityNumericId(selectedPrompt.id) : undefined;
  const promptVersionQuery = usePrompt(userToken, {
    workspace_id: workspaceId ?? 0,
    prompt_name: selectedPrompt?.name,
    number: selectedPrompt?.version_number,
    label: selectedPrompt?.label,
  });
  const activePresetStep: EvaluationPresetStep | null =
    step === "presets" || step === "prompt" ? null : step;
  const isPresets = step === "presets";

  const reset = () => {
    setStep("presets");
    setSelectedPreset(null);
    setSelectedPrompt(null);
    setIsSubmittingGenerateWithAI(false);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    onOpenChange(nextOpen);
    if (!nextOpen) {
      reset();
    }
  };

  const handleSelectPrompt = (prompt: Entity<EntityType.PROMPT | EntityType.SNIPPET>) => {
    setSelectedPrompt(prompt as Entity<EntityType.PROMPT>);
  };

  const handleSelectPreset = (preset: EvaluationPresetStep) => {
    posthog.capture("Smart Table Preset Selected", {
      preset_type: preset,
      source: "smart-table-create-dialog",
      workspace_id: workspaceId,
    });
    setSelectedPreset(preset);
    setStep(preset === "brand-visibility" || preset === "generate-with-ai" ? preset : "prompt");
  };

  const handleBack = () => {
    if (step === "prompt" || step === "brand-visibility" || step === "generate-with-ai") {
      setStep("presets");
      return;
    }

    setStep("prompt");
  };

  const handleCreateBlank = async () => {
    try {
      await createBlankTable();
      handleOpenChange(false);
    } catch (error) {
      displayErrorToast(error);
    }
  };

  const getPromptPayload = () => {
    if (!promptRegistryId || !promptVersionQuery.data?.number) {
      return null;
    }

    return {
      prompt_registry_id: promptRegistryId,
      prompt_version_number: promptVersionQuery.data.number,
    };
  };

  const handleSubmitModelComparison = (
    selections: ModelComparisonSelection[],
    initialInputs: Record<string, string>,
  ) => {
    const promptPayload = getPromptPayload();
    if (!promptPayload) {
      return;
    }

    const normalizedModels = dedupeModelLabels(
      selections.filter(selection => Boolean(selection.engine.model?.trim())),
    );

    if (normalizedModels.length < 2) {
      return;
    }

    const payload = {
      ...promptPayload,
      title: modelComparisonTableTitle(selectedPrompt?.name),
      folder_id: isInitializingExistingSheet ? null : (folderId ?? null),
      models: normalizedModels,
      initial_inputs: initialInputs,
    };

    if (isInitializingExistingSheet) {
      initializeModelComparisonPreset.mutate(payload, {
        onSuccess: sheet => {
          presetTarget?.onInitialized?.(sheet);
          handleOpenChange(false);
        },
        onError: error => displayErrorToast(error),
      });
      return;
    }

    createModelComparisonPreset.mutate(payload, {
      onSuccess: () => handleOpenChange(false),
      onError: error => displayErrorToast(error),
    });
  };

  const handleSubmitCustom = (initialInputs: Record<string, string>) => {
    const promptPayload = getPromptPayload();
    if (!promptPayload) {
      return;
    }

    const payload = {
      ...promptPayload,
      title: customTableTitle(selectedPrompt?.name),
      folder_id: isInitializingExistingSheet ? null : (folderId ?? null),
      initial_inputs: initialInputs,
    };

    if (isInitializingExistingSheet) {
      initializeCustomPreset.mutate(payload, {
        onSuccess: sheet => {
          presetTarget?.onInitialized?.(sheet);
          handleOpenChange(false);
        },
        onError: error => displayErrorToast(error),
      });
      return;
    }

    createCustomPreset.mutate(payload, {
      onSuccess: () => handleOpenChange(false),
      onError: error => displayErrorToast(error),
    });
  };

  const handleSubmitBrandVisibility = ({
    domain,
    queries,
    models,
  }: BrandVisibilityPresetSubmitPayload) => {
    const payload = {
      title: brandVisibilityTableTitle,
      folder_id: isInitializingExistingSheet ? null : (folderId ?? null),
      domain,
      queries,
      models,
    };

    if (isInitializingExistingSheet) {
      initializeBrandVisibilityPreset.mutate(payload, {
        onSuccess: sheet => {
          presetTarget?.onInitialized?.(sheet);
          handleOpenChange(false);
        },
        onError: error => displayErrorToast(error),
      });
      return;
    }

    createBrandVisibilityPreset.mutate(payload, {
      onSuccess: () => handleOpenChange(false),
      onError: error => displayErrorToast(error),
    });
  };

  const handleSubmitGenerateWithAI = async (rawMessage: string) => {
    const activeWorkspaceId = workspaceId;
    if (typeof activeWorkspaceId !== "number" || !userToken) {
      displayToast("Unable to start Wrangler AI right now.", ToastType.error);
      return;
    }

    if (chatStoreInstance.isLoading) {
      displayToast("Wrangler AI is already running.", ToastType.warning);
      return;
    }

    try {
      setIsSubmittingGenerateWithAI(true);
      chatStoreInstance.setAuthToken(userToken);
      chatStoreInstance.setFollowMode(true);
      const wranglerMessage = buildGenerateWithAIMessage({
        userRequest: rawMessage,
        presetTarget,
        folderId,
      });
      const message = await convertWorkspaceUrlsToTagsAsync(wranglerMessage, { userToken });

      if (!message.trim()) {
        displayToast("Enter a request for Wrangler AI.", ToastType.error);
        return;
      }

      const invalidateFolderEntities = () => {
        queryClient.invalidateQueries({ queryKey: ["folder-entities"] });
      };
      const invalidateAll = () => {
        queryClient.invalidateQueries({ queryKey: ["folder-entities"] });
        queryClient.invalidateQueries({ queryKey: ["recent-vibe-chat-sessions"] });
      };

      await chatStoreInstance.handleSendMessage(message, activeWorkspaceId, {
        onStreamComplete: invalidateAll,
        onAgentProgress: invalidateFolderEntities,
        onAgentComplete: invalidateAll,
        onToolResult: payload =>
          handleVibeChatToolResultInvalidations(queryClient, activeWorkspaceId, payload),
      });

      window.dispatchEvent(new Event(OPEN_VIBECHAT_EVENT));
      handleOpenChange(false);
    } catch (error) {
      displayErrorToast(error);
    } finally {
      setIsSubmittingGenerateWithAI(false);
    }
  };

  const handleSubmitEvaluation = ({
    presetType,
    initialInputs,
    expectedValue,
    assertions,
  }: {
    presetType: EvaluationPresetType;
    initialInputs: Record<string, string>;
    expectedValue?: string;
    assertions: string[];
  }) => {
    const promptPayload = getPromptPayload();
    if (!promptPayload) {
      return;
    }

    const payload = {
      ...promptPayload,
      preset_type: presetType,
      title: evaluationTableTitle(presetType, selectedPrompt?.name),
      folder_id: isInitializingExistingSheet ? null : (folderId ?? null),
      initial_inputs: initialInputs,
      expected_value: expectedValue,
      assertions,
    };

    if (isInitializingExistingSheet) {
      initializeEvaluationPreset.mutate(payload, {
        onSuccess: sheet => {
          presetTarget?.onInitialized?.(sheet);
          handleOpenChange(false);
        },
        onError: error => displayErrorToast(error),
      });
      return;
    }

    createEvaluationPreset.mutate(payload, {
      onSuccess: () => handleOpenChange(false),
      onError: error => displayErrorToast(error),
    });
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[86vh] w-[calc(100vw-1rem)] max-w-2xl gap-0 overflow-hidden p-0 sm:w-[calc(100vw-2rem)]">
        <div className="border-b border-gray-100 bg-white px-4 py-3.5 sm:px-5">
          <DialogHeader className="space-y-1 text-left">
            <div className="flex min-h-7 items-center gap-2">
              {!isPresets && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 shrink-0 text-gray-400 hover:text-gray-900"
                  onClick={handleBack}
                  aria-label={
                    step === "prompt" || step === "brand-visibility"
                      ? "Back to table presets"
                      : "Back to prompt selection"
                  }
                >
                  <ArrowLeft className="h-4 w-4" />
                </Button>
              )}
              <DialogTitle className="text-base font-semibold leading-6 text-gray-950">
                {stepTitle(step)}
              </DialogTitle>
            </div>
            <DialogDescription className="text-sm leading-5 text-gray-600">
              {stepDescription(step)}
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="overflow-hidden">
          <div
            className={`grid transition-[grid-template-rows,opacity,transform] duration-200 ease-out ${
              step === "presets"
                ? "translate-y-0 grid-rows-[1fr] opacity-100"
                : "pointer-events-none -translate-y-1 grid-rows-[0fr] opacity-0"
            }`}
          >
            <div className="min-h-0 overflow-hidden">
              <PresetGrid
                includeBlank={!isInitializingExistingSheet}
                isCreatingBlank={isCreatingBlankTable}
                onSelectModelComparison={() => handleSelectPreset("model-comparison")}
                onSelectGroundTruth={() => handleSelectPreset("GROUND_TRUTH_COMPARE")}
                onSelectStructural={() => handleSelectPreset("STRUCTURAL")}
                onSelectBrandVisibility={() => handleSelectPreset("brand-visibility")}
                onSelectCustom={() => handleSelectPreset("custom")}
                onSelectGenerateWithAI={() => handleSelectPreset("generate-with-ai")}
                onSelectBlank={handleCreateBlank}
              />
            </div>
          </div>

          <div
            className={`grid transition-[grid-template-rows,opacity,transform] duration-200 ease-out ${
              step === "prompt"
                ? "translate-y-0 grid-rows-[1fr] opacity-100"
                : "pointer-events-none translate-y-1 grid-rows-[0fr] opacity-0"
            }`}
          >
            <div className="min-h-0 overflow-hidden">
              <PromptStep
                selectedPrompt={selectedPrompt}
                promptVersion={promptVersionQuery.data}
                isLoadingPrompts={false}
                isLoadingPromptVersion={
                  Boolean(selectedPrompt) &&
                  (promptVersionQuery.isLoading || promptVersionQuery.isFetching)
                }
                onSelectPrompt={handleSelectPrompt}
                onContinue={() => selectedPreset && setStep(selectedPreset)}
              />
            </div>
          </div>

          <div
            className={`grid transition-[grid-template-rows,opacity,transform] duration-200 ease-out ${
              activePresetStep
                ? "translate-y-0 grid-rows-[1fr] opacity-100"
                : "pointer-events-none translate-y-1 grid-rows-[0fr] opacity-0"
            }`}
          >
            <div className="min-h-0 overflow-hidden">
              {activePresetStep !== "brand-visibility" &&
              activePresetStep !== "generate-with-ai" &&
              (promptVersionQuery.isLoading || promptVersionQuery.isFetching) ? (
                <div className="flex min-h-[280px] flex-col items-center justify-center px-6 text-center">
                  <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-xl bg-gray-50 text-gray-500 ring-1 ring-gray-100">
                    <FileText className="h-4 w-4" />
                  </div>
                  <p className="text-sm font-medium text-gray-900">Loading prompt version</p>
                  <p className="mt-1 max-w-sm text-xs leading-5 text-gray-500">
                    We’re preparing the input fields for this preset.
                  </p>
                </div>
              ) : (
                <EvaluationPreset
                  step={activePresetStep}
                  promptVersion={promptVersionQuery.data}
                  isSubmittingModelComparison={
                    createModelComparisonPreset.isPending ||
                    initializeModelComparisonPreset.isPending
                  }
                  isSubmittingCustom={
                    createCustomPreset.isPending || initializeCustomPreset.isPending
                  }
                  isSubmittingEvaluation={
                    createEvaluationPreset.isPending || initializeEvaluationPreset.isPending
                  }
                  isSubmittingBrandVisibility={
                    createBrandVisibilityPreset.isPending ||
                    initializeBrandVisibilityPreset.isPending
                  }
                  isSubmittingGenerateWithAI={isSubmittingGenerateWithAI}
                  onSubmitModelComparison={handleSubmitModelComparison}
                  onSubmitCustom={handleSubmitCustom}
                  onSubmitBrandVisibility={handleSubmitBrandVisibility}
                  onSubmitGenerateWithAI={handleSubmitGenerateWithAI}
                  onSubmitEvaluation={handleSubmitEvaluation}
                />
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SmartTableCreateDialog;
